<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col cols="6" class="text-center text-sm-left mb-4 mb-sm-0 ui-title-text">
        <span class="text-uppercase page-subtitle">Strona główna</span>
        <h3 class="page-title">Witaj w panelu</h3>
      </d-col>

      <d-col cols="6" class="text-right ui-title-buttons">
        <d-button
          theme="primary"
          class="mt-3"
          @click.prevent="debouncedLoadData"
          :disabled="!loaded">
          <template v-if="loaded">
            <i class="material-icons">refresh</i>
            <span class="ui-button-label">Odśwież</span>
          </template>
          <template v-if="!loaded">
            <div
              class="spinner-border spinner-border-sm"
              role="status"></div>
          </template>
        </d-button>

        <d-button
          theme="danger"
          class="mt-3 ml-3"
          @click.prevent="$router.push('/wyloguj')">
          <i class="material-icons">logout</i>
          <span class="ui-button-label">Wyloguj</span>
        </d-button>
      </d-col>
    </d-row>

    <d-row v-if="!emergencyLoaded">
      <d-alert
        show
        theme="light"
        class="text-center w-100">
        Trwa wczytywanie danych...
      </d-alert>
    </d-row>

    <d-row
      v-if="hasEmergencyItems"
      key="order-group-emergency"
      class="mb-4 px-3">
      <h4 class="pt-3 mb-4 mt-3 d-block w-100 text-warning">
        ⚠️ Zamówienia awaryjne
        <d-badge
          theme="warning"
          class="ml-2"
          style="position: relative; top: -2px;">
          {{ emergencyItemsCount }}
        </d-badge>
        <d-button
          theme="warning"
          outline
          size="sm"
          class="ml-2"
          style="position: relative; top: -2px;"
          @click.prevent="toggleEmergencyVisible">
          {{ emergencyVisible ? 'Ukryj' : 'Pokaż' }}
        </d-button>
      </h4>

      <d-card
        v-if="emergencyVisible"
        class="w-100 is-hidden-on-mobile">
        <table class="table mt-0 mb-0 full-width items-list">
          <thead class="bg-light">
            <tr>
              <th scope="col" class="border-0">Nazwa procesu</th>
              <th scope="col" class="border-0">Nazwa zamówienia</th>
              <th scope="col" class="border-0" style="width: 360px">Terminy</th>
              <th scope="col" class="border-0" style="width: 300px;">Produkt</th>
              <th scope="col" class="border-0" style="width: 300px;">Informacje dodatkowe</th>
              <th scope="col" class="border-0">Realizacja</th>
              <th v-if="visibleItems !== 'ended'" scope="col" class="border-0" style="width: 140px;">Wykonanie</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) of emergencyItems"
              :key="'emergency-item-' + index">
              <td class="align-middle">
                <small>
                  <strong>
                    <template v-if="item.process_type === 'PATTERN'">Wykrajanie</template>
                    <template v-else>{{ item.process_name }}</template>
                  </strong>
                </small>
              </td>

              <td class="align-middle">
                <d-badge
                  v-if="item.remote_job"
                  theme="success">
                  Praca zdalna
                </d-badge>
                <br v-if="item.remote_job">

                <small>
                  <strong>{{ item.order_name }}</strong>
                  <br>
                  ID zamówienia: #{{ item.order_id }}
                </small>
              </td>

              <td class="align-middle">
                <div v-if="item.remote_job">
                  <small>
                    <strong>Data dostawy:</strong>
                    {{ item.remote_job.planned_collection_date }}
                  </small>
                </div>
                <div v-if="item.process_type === 'PATTERN'">
                  <small>
                    <strong>
                      Otrzymano:
                    </strong>
                    {{ item.created_at }}
                  </small>
                </div>
                <div :class="dateColor(item.end_at)">
                  <small>
                    <strong>
                      Termin:
                    </strong>
                    {{ item.end_at }}
                  </small>
                </div>
                <div v-if="item.remote_job">
                  <small>
                    <strong>
                      Data odbioru:
                    </strong>
                    {{ item.remote_job.planned_delivery_date }}
                  </small>
                </div>

                <div v-if="item.schedules.length">
                  <small
                    v-if="!itemsEmergencyScheduleOpened[index]"
                    class="text-primary cursor-pointer"
                    @click="toggleSchedule(itemsEmergencyScheduleOpened, index, true)">
                    Zobacz obłożenie
                  </small>
                  <small
                    v-if="itemsEmergencyScheduleOpened[index]"
                    class="text-primary cursor-pointer"
                    @click="toggleSchedule(itemsEmergencyScheduleOpened, index, false)">
                    Schowaj obłożenie
                  </small>

                  <dl
                    v-if="itemsEmergencyScheduleOpened[index]"
                    class="mt-2">
                    <template v-for="(schedule, scheduleIndex) of item.schedules">
                    <dt :key="'schedule-emergency-title-' + scheduleIndex">{{ schedule.date }} ({{ Math.ceil(schedule.daily_percent) }}%)</dt>
                    <dd :key="'schedule-emergency-desc-' + scheduleIndex">Wykonano: {{ schedule.quantity_done }} z {{ schedule.quantity }} szt.</dd>
                    </template>
                  </dl>
                </div>
              </td>

              <td class="align-middle">
                <dl>
                  <dt>
                    Kod produktu:
                  </dt>
                  <dd>
                    {{ item.product_code }}
                  </dd>

                  <template v-if="item.process_type === 'PATTERN' && item.color_name">
                    <dt>
                      Kolor produktu:
                    </dt>
                    <dd>
                      {{ item.color_name }}
                    </dd>
                  </template>

                  <template v-if="item.process_type === 'PATTERN' && item.polar_name">
                    <dt>
                      Kolor polaru:
                    </dt>
                    <dd>
                      {{ item.polar_name }}
                    </dd>
                  </template>
                </dl>
              </td>

              <td class="align-middle">
                <dl v-if="item.process_type === 'PATTERN'">
                  <dt>
                    Wykrój:
                  </dt>
                  <dd>
                    <span
                      v-if="item.pattern_before_order"
                      class="text-success">
                      Tak
                    </span>
                    <span
                      v-else
                      class="text-danger">
                      Nie
                    </span>
                  </dd>

                  <dt>
                    Zostaje w Hafciarni:
                  </dt>
                  <dd>
                    <span
                      v-if="item.pattern_stays"
                      class="text-success">
                      Tak
                    </span>
                    <span
                      v-else
                      class="text-danger">
                      Nie
                    </span>
                  </dd>

                  <template v-if="item.seam_name">
                    <dt>
                      Przeszycia:
                    </dt>
                    <dd>
                      {{ item.seam_name }}
                    </dd>
                  </template>

                  <template v-if="item.size_name">
                    <dt>
                      Rozmiar:
                    </dt>
                    <dd>
                      {{ item.size_name }}
                    </dd>
                  </template>

                  <template v-if="item.material_name">
                    <dt>
                      Materiał:
                    </dt>
                    <dd>
                      {{ item.material_name }}
                    </dd>
                  </template>

                  <template v-if="item.special_requirement">
                    <dt>
                      Specjalne wymagania:
                    </dt>
                    <dd>
                      {{ item.special_requirement }}
                    </dd>
                  </template>

                  <template v-if="item.description">
                    <dt>
                      Uwagi:
                    </dt>
                    <dd>
                      {{ item.description }}
                    </dd>
                  </template>
                </dl>
                <dl v-else>
                  <template v-if="item.strand_name">
                    <dt>
                      Kolor nici:
                    </dt>
                    <dd>
                      {{ item.strand_name }}
                    </dd>
                  </template>

                  <template v-if="item.material_name">
                    <dt>
                      Materiał:
                    </dt>
                    <dd>
                      {{ item.material_name }}
                    </dd>
                  </template>

                  <template v-if="item.color_name">
                    <dt>
                      Kolor:
                    </dt>
                    <dd>
                      {{ item.color_name }}
                    </dd>
                  </template>

                  <template v-if="item.seam_name">
                    <dt>
                      Przeszycia:
                    </dt>
                    <dd>
                      {{ item.seam_name }}
                    </dd>
                  </template>

                  <template v-if="item.special_requirement">
                    <dt>
                      Specjalne wymagania:
                    </dt>
                    <dd>
                      {{ item.special_requirement }}
                    </dd>
                  </template>

                  <template v-if="item.description">
                    <dt>
                      Uwagi:
                    </dt>
                    <dd>
                      {{ item.description }}
                    </dd>
                  </template>
                </dl>
              </td>

              <td class="align-middle">
                <span
                  :class="{
                    'text-success': item.quantity_done + item.quantity_additional >= item.quantity,
                    'text-danger': item.quantity_done + item.quantity_additional < item.quantity && item.quantity_done + item.quantity_additional > 0,
                    'd-block': true
                  }">
                  {{ item.quantity_done + item.quantity_additional }} z {{ item.quantity }} szt.
                </span>
                <small
                  v-if="item.quantity_additional"
                  class="d-block text-primary">
                  W tym Ekstra: {{ item.quantity_additional }} szt.
                </small>
                <small
                  v-if="item.quantity_shortage"
                  class="d-block text-danger">
                  Braki: {{ item.quantity_shortage }} szt.
                </small>
              </td>

              <td
                v-if="visibleItems !== 'ended'"
                class="align-middle">
                <d-button
                  theme="success"
                  class="mt-2 w-100"
                  size="sm"
                  @click.prevent="openPopup(item, true)">
                  Aktualizuj
                </d-button>
              </td>
            </tr>
          </tbody>
        </table>
      </d-card>

      <template v-if="emergencyVisible">
      <d-card
        v-for="(item, index) of emergencyItems"
        :key="'emergency-mobile-item-' + index"
        class="w-100 p-3 mb-3 is-visible-on-mobile">
        <d-badge
          v-if="item.remote_job"
          theme="success">
          Praca zdalna
        </d-badge>
        <br v-if="item.remote_job">

        <strong>Nazwa procesu:</strong>
        <br>
        <small>
          <strong>
            <template v-if="item.process_type === 'PATTERN'">Wykrajanie</template>
            <template v-else>{{ item.process_name }}</template>
          </strong>
        </small>
        <br>
        <strong>Nazwa zamówienia:</strong>
        <br>
        <small>
          <strong>{{ item.order_name }}</strong>
        </small>
        <br>
        <small>
          ID zamówienia: #{{ item.order_id }}
        </small>

        <hr class="my-2">

        <small>
          <template v-if="item.remote_job">
            <strong>Data dostawy:</strong> {{ item.remote_job.planned_collection_date }}<br>
          </template>
          <template v-if="item.process_type === 'PATTERN'">
            <strong>Otrzymano:</strong> <span>{{ item.created_at }}</span><br>
          </template>
          <strong>Termin:</strong> <span :class="dateColor(item.end_at)">{{ item.end_at }}</span>
          <template v-if="item.remote_job">
            <br><strong>Data odbioru:</strong> {{ item.remote_job.planned_delivery_date }}
          </template>
        </small>

        <hr
          v-if="item.schedules.length"
          class="my-2">

        <div v-if="item.schedules.length">
          <small
            v-if="!itemsEmergencyScheduleOpened[index]"
            class="text-primary cursor-pointer"
            @click="toggleSchedule(itemsEmergencyScheduleOpened, index, true)">
            Zobacz obłożenie
          </small>
          <small
            v-if="itemsEmergencyScheduleOpened[index]"
            class="text-primary cursor-pointer"
            @click="toggleSchedule(itemsEmergencyScheduleOpened, index, false)">
            Schowaj obłożenie
          </small>

          <dl
            v-if="itemsEmergencyScheduleOpened[index]"
            class="mt-2">
            <template v-for="(schedule, scheduleIndex) of item.schedules">
            <dt :key="'schedule-emergency-mob-title-' + scheduleIndex">{{ schedule.date }} ({{ Math.ceil(schedule.daily_percent) }}%)</dt>
            <dd :key="'schedule-emergency-mob-desc-' + scheduleIndex">Wykonano: {{ schedule.quantity_done }} z {{ schedule.quantity }} szt.</dd>
            </template>
          </dl>
        </div>

        <hr class="my-2">

        <small>
          <strong>Kod produktu:</strong> {{ item.product_code }}

          <template v-if="item.process_type === 'PATTERN' && item.color_name">
            <br>
            <strong>Kolor produktu:</strong> {{ item.color_name }}
          </template>

          <template v-if="item.process_type === 'PATTERN' && item.polar_name">
            <br>
            <strong>Kolor polaru:</strong> {{ item.polar_name }}
          </template>
        </small>

        <hr class="my-2">

        <small v-if="item.process_type === 'PATTERN'">
          <strong>Wykrój:</strong>
          <span v-if="item.pattern_before_order" class="text-success">Tak</span>
          <span v-else class="text-danger">Nie</span><br>

          <strong>Zostaje w Hafciarni:</strong>
          <span v-if="item.pattern_stays" class="text-success">Tak</span>
          <span v-else class="text-danger">Nie</span>

          <template v-if="item.seam_name">
            <br>
            <strong>Przeszycia:</strong> {{ item.seam_name }}
          </template>

          <template v-if="item.size_name">
            <br>
            <strong>Rozmiar:</strong> {{ item.size_name }}
          </template>

          <template v-if="item.material_name">
            <br>
            <strong>Materiał:</strong> {{ item.material_name }}
          </template>

          <template v-if="item.special_requirement">
            <br>
            <strong>Specjalne wymagania:</strong><br>
            {{ item.special_requirement }}
          </template>

          <template v-if="item.description">
            <br>
            <strong>Uwagi:</strong><br>
            {{ item.description }}
          </template>
        </small>
        <small v-else>
          <template v-if="item.strand_name">
            <strong>Kolor nici:</strong> {{ item.strand_name }}<br>
          </template>

          <template v-if="item.material_name">
            <strong>Materiał:</strong> {{ item.material_name }}<br>
          </template>

          <template v-if="item.color_name">
            <strong>Kolor:</strong> {{ item.color_name }}<br>
          </template>

          <template v-if="item.seam_name">
            <strong>Przeszycia:</strong> {{ item.seam_name }}<br>
          </template>

          <template v-if="item.special_requirement">
            <strong>Specjalne wymagania:</strong><br>
            {{ item.special_requirement }}<br>
          </template>

          <template v-if="item.description">
            <strong>Uwagi:</strong><br>
            {{ item.description }}
          </template>
        </small>

        <hr class="my-2">

        <d-row>
          <d-col cols="7" class="d-flex align-items-center">
            <small class="mt-1">
              <strong>
                Realizacja:
                <span
                  :class="{
                    'text-success': item.quantity_done + item.quantity_additional >= item.quantity,
                    'text-danger': item.quantity_done + item.quantity_additional < item.quantity && item.quantity_done + item.quantity_additional > 0
                  }">
                  {{ item.quantity_done + item.quantity_additional }} z {{ item.quantity }} szt.
                </span>
                <small
                  v-if="item.quantity_additional"
                  class="d-block text-primary">
                  W tym Ekstra: {{ item.quantity_additional }} szt.
                </small>
                <small
                  v-if="item.quantity_shortage"
                  class="d-block text-danger">
                  Braki: {{ item.quantity_shortage }} szt.
                </small>
              </strong>
            </small>
          </d-col>
          <d-col
            v-if="visibleItems !== 'ended'"
            cols="5">
            <d-button
              theme="success"
              class="mt-2 w-100"
              size="sm"
              @click.prevent="openPopup(item, true)">
              Aktualizuj
            </d-button>
          </d-col>
        </d-row>
      </d-card>
      </template>
    </d-row>

    <d-card class="mb-4">
      <d-card-body class="p-3 d-flex">
        <d-button-group class="mr-2">
          <d-button
            theme="success"
            :outline="visibleItems !== 'current'"
            @click.prevent="visibleItems = 'current'">
            Obecne zadania
          </d-button>
          <d-button
            theme="danger"
            :outline="visibleItems !== 'ended'"
            @click.prevent="visibleItems = 'ended'">
            Zakończone zadania
          </d-button>
        </d-button-group>

        <d-button
          class="ml-auto"
          @click.prevent="showFilters ? showFilters = false : showFilters = true;">
          <template v-if="showFilters">
            Ukryj filtry
          </template>
          <template v-else>
            Szukaj
          </template>
        </d-button>
      </d-card-body>
    </d-card>

    <d-card v-if="showFilters">
      <d-card-body class="pt-3 pb-2 pl-1 pr-2">
        <validation-observer
          ref="process-items-filters-form"
          tag="form"
          style="width: 100%;"
          class="d-flex flex-column-mobile">
          <d-col cols="10">
            <d-row>
              <d-col cols="6">
                <d-input
                  placeholder="Podaj kod produktu"
                  class="mb-2"
                  v-model="filters.product_code" />

                <d-input
                  placeholder="Podaj nazwę procesu"
                  class="mb-2"
                  v-model="filters.process_name" />
              </d-col>

              <d-col cols="6">
                <d-input
                  placeholder="Podaj nazwę zamówienia"
                  class="mb-2"
                  v-model="filters.order_name" />

                <d-input
                  placeholder="Podaj numer zamówienia"
                  class="mb-2"
                  v-model="filters.order_id" />
              </d-col>
            </d-row>

            <d-row
              v-if="visibleItems === 'ended'"
              class="w-100 pl-3 pb-1">
              <d-input-group
                class="w-100"
                prepend="Pokaż dane z okresu:">
                <date-range-picker
                  ref="picker"
                  :locale-data="{
                    'firstDay': 1,
                    'format': 'DD-MM-YYYY',
                    'daysOfWeek': ['Ndz', 'Pon', 'Wto', 'Śro', 'Czw', 'Ptk', 'Sob'],
                    'monthNames': ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
                  }"
                  :singleDatePicker="false"
                  :timePicker="false"
                  :showWeekNumbers="false"
                  :showDropdowns="false"
                  :autoApply="true"
                  v-model="range"
                  :ranges="false"
                  :linkedCalendars="true">
                  <template v-slot:input="picker" style="min-width: 350px;">
                    {{ picker.startDate | dateStart }} - {{ picker.endDate | dateEnd }}
                  </template>
                </date-range-picker>
              </d-input-group>
            </d-row>
          </d-col>

          <d-col cols="2">
            <d-button
              theme="info"
              outline
              class="text-nowrap mb-2 w-100"
              @click.prevent="validateFiltersForm">Szukaj</d-button>

            <d-button
              theme="danger"
              outline
              class="text-nowrap w-100"
              @click.prevent="clearFilters">Resetuj filtry</d-button>
          </d-col>
        </validation-observer>
      </d-card-body>
    </d-card>

    <d-alert
      :show="moreItemsExist"
      theme="warning"
      class="text-center mt-4">
      Obecnie pokazywane jest 50 ostatnich pasujących zleceń, ale istnieje ich więcej. Zawęź zakres wyszukiwania używając filtrów aby przejrzeć inne zlecenia.
    </d-alert>

    <d-row v-if="!loaded">
      <d-alert
        show
        theme="light"
        class="text-center w-100">
        Trwa wczytywanie danych...
      </d-alert>
    </d-row>

    <template v-if="loaded">
      <d-row
        v-if="!hasPatternItems && !hasSewingItems">
        <d-alert
          show
          theme="warning"
          class="text-center w-100 mt-4">
          Brak zleceń do wykonania...
        </d-alert>
      </d-row>

      <d-row
        v-if="hasPatternItems"
        key="order-group-pattern"
        class="my-4 px-3">
        <h4 class="pt-3 mb-2 mt-3 d-block w-100">
          Wykrajanie
          <d-badge
            class="ml-2"
            style="position: relative; top: -2px;">
            {{ patternItemsCount }}
          </d-badge>
        </h4>
        <h5 class="d-block">
          Zadania dla:
          <d-badge
            class="ml-2 mb-4"
            theme="warning"
            style="position: relative; top: -1px;">
            {{ currentUserName }}
          </d-badge>
        </h5>

        <d-card class="w-100 is-hidden-on-mobile">
          <table class="table mt-0 mb-0 full-width items-list">
            <thead class="bg-light">
              <tr>
                <th scope="col" class="border-0">Nazwa zamówienia</th>
                <th scope="col" class="border-0" style="width: 200px">Terminy</th>
                <th scope="col" class="border-0" style="width: 300px;">Produkt</th>
                <th scope="col" class="border-0" style="width: 300px;">Informacje dodatkowe</th>
                <th scope="col" class="border-0" style="width: 160px;">Realizacja</th>
                <th v-if="visibleItems !== 'ended'" scope="col" class="border-0" style="width: 140px;">Wykonanie</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) of patternItems"
                :key="'pattern-item-' + index">
                <td class="align-middle">
                  <d-badge
                    v-if="item.remote_job"
                    theme="success">
                    Praca zdalna
                  </d-badge>
                  <br v-if="item.remote_job">

                  <small>
                    <strong>{{ item.order_name }}</strong>
                    <br>
                    ID zamówienia: #{{ item.order_id }}
                  </small>
                </td>

                <td class="align-middle">
                  <div v-if="item.remote_job">
                    <small>
                      <strong>Data dostawy:</strong>
                      {{ item.remote_job.planned_collection_date }}
                    </small>
                  </div>
                  <div>
                    <small>
                      <strong>
                        Otrzymano:
                      </strong>
                      {{ item.created_at }}
                    </small>
                  </div>
                  <div :class="dateColor(item.end_at)">
                    <small>
                      <strong>
                        Termin:
                      </strong>
                      {{ item.end_at }}
                    </small>
                  </div>
                  <div v-if="item.remote_job">
                    <small>
                      <strong>
                        Data odbioru:
                      </strong>
                      {{ item.remote_job.planned_delivery_date }}
                    </small>
                  </div>

                  <div v-if="item.schedules.length">
                    <small
                      v-if="!itemsScheduleOpened[index]"
                      class="text-primary cursor-pointer"
                      @click="toggleSchedule(itemsScheduleOpened, index, true)">
                      Zobacz obłożenie
                    </small>
                    <small
                      v-if="itemsScheduleOpened[index]"
                      class="text-primary cursor-pointer"
                      @click="toggleSchedule(itemsScheduleOpened, index, false)">
                      Schowaj obłożenie
                    </small>

                    <dl
                      v-if="itemsScheduleOpened[index]"
                      class="mt-2">
                      <template v-for="(schedule, index) of item.schedules">
                      <dt :key="'schedule-title-' + index">{{ schedule.date }} ({{ Math.ceil(schedule.daily_percent) }}%)</dt>
                      <dd :key="'schedule-desc-' + index">Wykonano: {{ schedule.quantity_done }} z {{ schedule.quantity }} szt.</dd>
                      </template>
                    </dl>
                  </div>
                </td>

                <td class="align-middle">
                  <dl>
                    <dt>
                      Kod produktu:
                    </dt>
                    <dd>
                      {{ item.product_code }}
                    </dd>

                    <template v-if="item.color_name">
                      <dt>
                        Kolor produktu:
                      </dt>
                      <dd>
                        {{ item.color_name }}
                      </dd>
                    </template>

                    <template v-if="item.polar_name">
                      <dt>
                        Kolor polaru:
                      </dt>
                      <dd>
                        {{ item.polar_name }}
                      </dd>
                    </template>
                  </dl>
                </td>

                <td class="align-middle">
                  <dl>
                    <dt>
                      Wykrój:
                    </dt>
                    <dd>
                      <span
                        v-if="item.pattern_before_order"
                        class="text-success">
                        Tak
                      </span>
                      <span
                        v-else
                        class="text-danger">
                        Nie
                      </span>
                    </dd>

                    <dt>
                      Zostaje w Hafciarni:
                    </dt>
                    <dd>
                      <span
                        v-if="item.pattern_stays"
                        class="text-success">
                        Tak
                      </span>
                      <span
                        v-else
                        class="text-danger">
                        Nie
                      </span>
                    </dd>

                    <template v-if="item.seam_name">
                      <dt>
                        Przeszycia:
                      </dt>
                      <dd>
                        {{ item.seam_name }}
                      </dd>
                    </template>

                    <template v-if="item.size_name">
                      <dt>
                        Rozmiar:
                      </dt>
                      <dd>
                        {{ item.size_name }}
                      </dd>
                    </template>

                    <template v-if="item.material_name">
                      <dt>
                        Materiał:
                      </dt>
                      <dd>
                        {{ item.material_name }}
                      </dd>
                    </template>

                    <template v-if="item.special_requirement">
                      <dt>
                        Specjalne wymagania:
                      </dt>
                      <dd>
                        {{ item.special_requirement }}
                      </dd>
                    </template>

                    <template v-if="item.description">
                      <dt>
                        Uwagi:
                      </dt>
                      <dd>
                        {{ item.description }}
                      </dd>
                    </template>
                  </dl>
                </td>

                <td class="align-middle">
                  <span
                    :class="{
                      'text-success': item.quantity_done + item.quantity_additional >= item.quantity,
                      'text-danger': item.quantity_done + item.quantity_additional < item.quantity && item.quantity_done + item.quantity_additional > 0,
                      'd-block': true
                    }">
                    {{ item.quantity_done + item.quantity_additional }} z {{ item.quantity }} szt.
                  </span>
                  <small
                    v-if="item.quantity_additional"
                    class="d-block text-primary">
                    W tym Ekstra: {{ item.quantity_additional }} szt.
                  </small>
                  <small
                    v-if="item.quantity_shortage"
                    class="d-block text-danger">
                    Braki: {{ item.quantity_shortage }} szt.
                  </small>
                </td>

                <td
                  v-if="visibleItems !== 'ended'"
                  class="align-middle">
                  <d-button
                    theme="success"
                    class="mt-2 w-100"
                    size="sm"
                    @click.prevent="openPopup(item)">
                    Aktualizuj
                  </d-button>
                </td>
              </tr>
            </tbody>
          </table>
        </d-card>

        <d-card
          v-for="(item, index) of patternItems"
          :key="'pattern-mobile-item-' + index"
          class="w-100 p-3 mb-3 is-visible-on-mobile">
          <d-badge
            v-if="item.remote_job"
            theme="success">
            Praca zdalna
          </d-badge>
          <br v-if="item.remote_job">

          <strong>Nazwa zamówienia:</strong><br>
          <small>
            <strong>{{ item.order_name }}</strong>
            <br>
            ID zamówienia: #{{ item.order_id }}
          </small>

          <hr class="my-2">

          <small>
            <template v-if="item.remote_job">
              <strong>Data dostawy:</strong> {{ item.remote_job.planned_collection_date }}<br>
            </template>
            <strong>Otrzymano:</strong> <span>{{ item.created_at }}</span><br>
            <strong>Termin:</strong> <span :class="dateColor(item.end_at)">{{ item.end_at }}</span>
            <template v-if="item.remote_job">
              <br><strong>Data odbioru:</strong> {{ item.remote_job.planned_delivery_date }}
            </template>
          </small>

          <hr
            v-if="item.schedules.length"
            class="my-2">

          <div v-if="item.schedules.length">
            <small
              v-if="!itemsScheduleOpened[index]"
              class="text-primary cursor-pointer"
              @click="toggleSchedule(itemsScheduleOpened, index, true)">
              Zobacz obłożenie
            </small>
            <small
              v-if="itemsScheduleOpened[index]"
              class="text-primary cursor-pointer"
              @click="toggleSchedule(itemsScheduleOpened, index, false)">
              Schowaj obłożenie
            </small>

            <dl
              v-if="itemsScheduleOpened[index]"
              class="mt-2">
              <template v-for="(schedule, index) of item.schedules">
              <dt :key="'schedule-mob-title-' + index">{{ schedule.date }} ({{ Math.ceil(schedule.daily_percent) }}%)</dt>
              <dd :key="'schedule-mob-desc-' + index">Wykonano: {{ schedule.quantity_done }} z {{ schedule.quantity }} szt.</dd>
              </template>
            </dl>
          </div>

          <hr class="my-2">

          <small>
            <strong>Kod produktu:</strong> {{ item.product_code }}

            <template v-if="item.color_name">
              <br>
              <strong>Kolor produktu:</strong> {{ item.color_name }}<br>
            </template>

            <template v-if="item.polar_name">
              <br>
              <strong>Kolor polaru:</strong> {{ item.polar_name }}
            </template>
          </small>

          <hr class="my-2">

          <small>
            <strong>Wykrój:</strong>
            <span v-if="item.pattern_before_order" class="text-success">Tak</span>
            <span v-else class="text-danger">Nie</span><br>

            <strong>Zostaje w Hafciarni:</strong>
            <span v-if="item.pattern_stays" class="text-success">Tak</span>
            <span v-else class="text-danger">Nie</span>

            <template v-if="item.seam_name">
              <br>
              <strong>Przeszycia:</strong> {{ item.seam_name }}
            </template>

            <template v-if="item.size_name">
              <br>
              <strong>Rozmiar:</strong> {{ item.size_name }}
            </template>

            <template v-if="item.material_name">
              <br>
              <strong>Materiał:</strong> {{ item.material_name }}
            </template>

            <template v-if="item.special_requirement">
              <br>
              <strong>Specjalne wymagania:</strong><br>
              {{ item.special_requirement }}
            </template>

            <template v-if="item.description">
              <br>
              <strong>Uwagi:</strong><br>
              {{ item.description }}
            </template>
          </small>

          <hr class="my-2">

          <d-row>
            <d-col cols="7" class="d-flex align-items-center">
              <small class="mt-1">
                <strong>
                  Realizacja:
                  <span
                    :class="{
                      'text-success': item.quantity_done + item.quantity_additional >= item.quantity,
                      'text-danger': item.quantity_done + item.quantity_additional < item.quantity && item.quantity_done + item.quantity_additional > 0
                    }">
                    {{ item.quantity_done + item.quantity_additional }} z {{ item.quantity }} szt.
                  </span>
                  <small
                    v-if="item.quantity_additional"
                    class="d-block text-primary">
                    W tym Ekstra: {{ item.quantity_additional }} szt.
                  </small>
                  <small
                    v-if="item.quantity_shortage"
                    class="d-block text-danger">
                    Braki: {{ item.quantity_shortage }} szt.
                  </small>
                </strong>
              </small>
            </d-col>
            <d-col
              v-if="visibleItems !== 'ended'"
              cols="5">
              <d-button
                theme="success"
                class="mt-2 w-100"
                size="sm"
                @click.prevent="openPopup(item)">
                Aktualizuj
              </d-button>
            </d-col>
          </d-row>
        </d-card>
      </d-row>

      <d-row
        v-if="hasSewingItems"
        key="order-group-sewing"
        class="my-4 px-3">
        <h4 class="pt-3 mb-2 mt-3 d-block w-100">
          Szycie
          <d-badge
            class="ml-2"
            style="position: relative; top: -2px;">
            {{ sewingItemsCount }}
          </d-badge>
        </h4>

        <h5 class="d-block">
          Zadania dla:
          <d-badge
            class="ml-2 mb-4"
            theme="warning"
            style="position: relative; top: -1px;">
            {{ currentUserName }}
          </d-badge>
        </h5>

        <d-card class="w-100 is-hidden-on-mobile">
          <validation-observer
            ref="sewing-forms"
            tag="div">
            <table class="table mt-0 mb-0 full-width items-list">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0">Nazwa procesu</th>
                  <th scope="col" class="border-0">Nazwa zamówienia</th>
                  <th scope="col" class="border-0" style="width: 360px">Terminy</th>
                  <th scope="col" class="border-0" style="width: 300px;">Produkt</th>
                  <th scope="col" class="border-0" style="width: 300px;">Informacje dodatkowe</th>
                  <th scope="col" class="border-0">Realizacja</th>
                  <th v-if="visibleItems !== 'ended'" scope="col" class="border-0" style="width: 140px;">Wykonanie</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) of sewingItems"
                  :key="'sewing-item-' + index">
                  <td class="align-middle">
                    <small>
                      <strong>{{ item.process_name }}</strong>
                    </small>
                  </td>

                  <td class="align-middle">
                    <d-badge
                      v-if="item.remote_job"
                      theme="success">
                      Praca zdalna
                    </d-badge>
                    <br v-if="item.remote_job">

                    <small>
                      <strong>{{ item.order_name }}</strong>
                      <br>
                      ID zamówienia: #{{ item.order_id }}
                    </small>
                  </td>

                  <td class="align-middle">
                    <div v-if="item.remote_job">
                      <small>
                        <strong>Data dostawy:</strong>
                        {{ item.remote_job.planned_collection_date }}
                      </small>
                    </div>
                    <div :class="dateColor(item.end_at)">
                      <small>
                        <strong>
                          Termin:
                        </strong>
                        {{ item.end_at }}
                      </small>
                    </div>
                    <div v-if="item.remote_job">
                      <small>
                        <strong>
                          Data odbioru:
                        </strong>
                        {{ item.remote_job.planned_delivery_date }}
                      </small>
                    </div>

                    <div v-if="item.schedules.length">
                      <small
                        v-if="!itemsSewingScheduleOpened[index]"
                        class="text-primary cursor-pointer"
                        @click="toggleSchedule(itemsSewingScheduleOpened, index, true)">
                        Zobacz obłożenie
                      </small>
                      <small
                        v-if="itemsSewingScheduleOpened[index]"
                        class="text-primary cursor-pointer"
                        @click="toggleSchedule(itemsSewingScheduleOpened, index, false)">
                        Schowaj obłożenie
                      </small>

                      <dl
                        v-if="itemsSewingScheduleOpened[index]"
                        class="mt-2">
                        <template v-for="(schedule, index) of item.schedules">
                        <dt :key="'schedule-sewing-title-' + index">{{ schedule.date }} ({{ Math.ceil(schedule.daily_percent) }}%)</dt>
                        <dd :key="'schedule-sewing-desc-' + index">Wykonano: {{ schedule.quantity_done }} z {{ schedule.quantity }} szt.</dd>
                        </template>
                      </dl>
                    </div>
                  </td>

                  <td class="align-middle">
                    <dl>
                      <dt>
                        Kod produktu:
                      </dt>
                      <dd>
                        {{ item.product_code }}
                      </dd>
                    </dl>
                  </td>

                  <td class="align-middle">
                    <dl>
                      <template v-if="item.strand_name">
                        <dt>
                          Kolor nici:
                        </dt>
                        <dd>
                          {{ item.strand_name }}
                        </dd>
                      </template>

                      <template v-if="item.material_name">
                        <dt>
                          Materiał:
                        </dt>
                        <dd>
                          {{ item.material_name }}
                        </dd>
                      </template>

                      <template v-if="item.color_name">
                        <dt>
                          Kolor:
                        </dt>
                        <dd>
                          {{ item.color_name }}
                        </dd>
                      </template>

                      <template v-if="item.seam_name">
                        <dt>
                          Przeszycia:
                        </dt>
                        <dd>
                          {{ item.seam_name }}
                        </dd>
                      </template>

                      <template v-if="item.special_requirement">
                        <dt>
                          Specjalne wymagania:
                        </dt>
                        <dd>
                          {{ item.special_requirement }}
                        </dd>
                      </template>

                      <template v-if="item.description">
                        <dt>
                          Uwagi:
                        </dt>
                        <dd>
                          {{ item.description }}
                        </dd>
                      </template>
                    </dl>
                  </td>

                  <td class="align-middle">
                    <span
                      :class="{
                        'text-success': item.quantity_done + item.quantity_additional >= item.quantity,
                        'text-danger': item.quantity_done + item.quantity_additional < item.quantity && item.quantity_done + item.quantity_additional > 0
                      }">
                      {{ item.quantity_done + item.quantity_additional }} z {{ item.quantity }} szt.
                    </span>
                    <small
                      v-if="item.quantity_additional"
                      class="d-block text-primary">
                      W tym Ekstra: {{ item.quantity_additional }} szt.
                    </small>
                    <small
                      v-if="item.quantity_shortage"
                      class="d-block text-danger">
                      Braki: {{ item.quantity_shortage }} szt.
                    </small>
                  </td>

                  <td
                    v-if="visibleItems !== 'ended'"
                    class="align-middle">
                    <d-button
                      theme="success"
                      class="mt-2 w-100"
                      size="sm"
                      @click.prevent="openPopup(item)">
                      Aktualizuj
                    </d-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </validation-observer>
        </d-card>

        <d-card
          v-for="(item, index) of sewingItems"
          :key="'sewing-mobile-item-' + index"
          class="w-100 p-3 mb-3 is-visible-on-mobile">
          <d-badge
            v-if="item.remote_job"
            theme="success">
            Praca zdalna
          </d-badge>
          <br v-if="item.remote_job">

          <strong>Nazwa procesu:</strong>
          <br>
          <small>
            <strong>{{ item.process_name }}</strong>
          </small>
          <br>
          <strong>Nazwa zamówienia:</strong>
          <br>
          <small>
            <strong>{{ item.order_name }}</strong>
          </small>
          <br>
          <small>
            ID zamówienia: #{{ item.order_id }}
          </small>

          <hr class="my-2">

          <small>
            <template v-if="item.remote_job">
              <strong>Data dostawy:</strong> {{ item.remote_job.planned_collection_date }}<br>
            </template>
            <strong>Termin:</strong> <span :class="dateColor(item.end_at)">{{ item.end_at }}</span>
            <template v-if="item.remote_job">
              <br><strong>Data odbioru:</strong> {{ item.remote_job.planned_delivery_date }}
            </template>
          </small>

          <hr
            v-if="item.schedules.length"
            class="my-2">

          <div v-if="item.schedules.length">
            <small
              v-if="!itemsSewingScheduleOpened[index]"
              class="text-primary cursor-pointer"
              @click="toggleSchedule(itemsSewingScheduleOpened, index, true)">
              Zobacz obłożenie
            </small>
            <small
              v-if="itemsSewingScheduleOpened[index]"
              class="text-primary cursor-pointer"
              @click="toggleSchedule(itemsSewingScheduleOpened, index, false)">
              Schowaj obłożenie
            </small>

            <dl
              v-if="itemsSewingScheduleOpened[index]"
              class="mt-2">
              <template v-for="(schedule, index) of item.schedules">
              <dt :key="'schedule-sewing-mob-title-' + index">{{ schedule.date }} ({{ Math.ceil(schedule.daily_percent) }}%)</dt>
              <dd :key="'schedule-sewing-mob-desc-' + index">Wykonano: {{ schedule.quantity_done }} z {{ schedule.quantity }} szt.</dd>
              </template>
            </dl>
          </div>

          <hr class="my-2">

          <small>
            <strong>Kod produktu:</strong> {{ item.product_code }}
          </small>

          <hr class="my-2">

          <small>
            <template v-if="item.strand_name">
              <br>
              <strong>Kolor nici:</strong> {{ item.strand_name }}
            </template>

            <template v-if="item.material_name">
              <br>
              <strong>Materiał:</strong> {{ item.material_name }}
            </template>

            <template v-if="item.color_name">
              <br>
              <strong>Kolor:</strong> {{ item.color_name }}
            </template>

            <template v-if="item.seam_name">
              <br>
              <strong>Przeszycia:</strong> {{ item.seam_name }}
            </template>

            <template v-if="item.special_requirement">
              <br>
              <strong>Specjalne wymagania:</strong><br>
              {{ item.special_requirement }}
            </template>

            <template v-if="item.description">
              <br>
              <strong>Uwagi:</strong><br>
              {{ item.description }}
            </template>
          </small>

          <hr class="my-2">

          <d-row>
            <d-col cols="7" class="d-flex align-items-center">
              <small class="mt-1">
                <strong>
                  Realizacja:
                  <span
                    :class="{
                      'text-success': item.quantity_done + item.quantity_additional >= item.quantity,
                      'text-danger': item.quantity_done + item.quantity_additional < item.quantity && item.quantity_done + item.quantity_additional > 0
                    }">
                    {{ item.quantity_done + item.quantity_additional }} z {{ item.quantity }} szt.
                  </span>
                  <small
                    v-if="item.quantity_additional"
                    class="d-block text-primary">
                    W tym Ekstra: {{ item.quantity_additional }} szt.
                  </small>
                  <small
                    v-if="item.quantity_shortage"
                    class="d-block text-danger">
                    Braki: {{ item.quantity_shortage }} szt.
                  </small>
                </strong>
              </small>
            </d-col>
            <d-col cols="5">
              <d-button
                v-if="visibleItems !== 'ended'"
                theme="success"
                class="mt-2 w-100"
                size="sm"
                @click.prevent="openPopup(item)">
                Aktualizuj
              </d-button>
            </d-col>
          </d-row>
        </d-card>
      </d-row>
    </template>

    <d-modal
      v-if="updatePopupVisible"
      class="realisation-form-popup"
      @close="closePopup">
      <d-modal-header>
        <d-modal-title>
          Aktualizuj stan realizacji
        </d-modal-title>
      </d-modal-header>
      <d-modal-body class="px-3 py-3">
        <validation-observer
          ref="realisation-form"
          tag="form">
          <p class="mb-3">
            Zrealizowano dotąd: {{ popupState.quantity_done + popupState.quantity_done_extra }} z {{ popupState.quantity }} szt.
            <span
              v-if="popupState.quantity_done_extra"
              class="text-primary">
              (w tym ekstra: {{ popupState.quantity_done_extra }} szt.)
            </span>
          </p>

          <validation-provider
            :rules="'required|min_value:1|max_value:' + (popupState.quantity - popupState.quantity_done)"
            name="items-done"
            v-slot="{ errors }">
            <div class="w-100 mb-2">
              <d-input-group
                append="szt."
                prepend="Oznacz jako zrealizowane:"
                :state="errors.length ? 'invalid' : null">
                <d-input-group-addon
                  v-if="popupState.done !== missingItems"
                  append
                  class="check-button-wrapper">
                    <d-btn
                      id="check-button"
                      theme="success"
                      @click.prevent="addMissingItems()">
                      <i class="material-icons">check</i>
                    </d-btn>
                    <d-tooltip
                      target="#check-button"
                      container=".check-button-wrapper">
                      Dodaj brakujące {{ missingItems }} szt.
                  </d-tooltip>
                </d-input-group-addon>
                <d-input
                  type="number"
                  :min="1"
                  :step="1"
                  :max="popupState.quantity - popupState.quantity_done"
                  :state="errors.length ? 'invalid' : null"
                  class="text-right"
                  v-model="popupState.done" />
              </d-input-group>
              <div class="invalid-feedback text-center">
                {{ errors[0] }}
              </div>
            </div>
          </validation-provider>

          <validation-provider
            :rules="'required|min_value:0'"
            name="items-additional"
            v-slot="{ errors }">
            <div class="w-100 mb-2">
              <d-input-group
                append="szt."
                prepend="Sztuki ekstra:"
                :state="errors.length ? 'invalid' : null">
                <d-input
                  type="number"
                  :min="0"
                  :step="1"
                  :state="errors.length ? 'invalid' : null"
                  class="text-right"
                  v-model="popupState.quantity_additional" />
              </d-input-group>
              <div class="invalid-feedback text-center">
                {{ errors[0] }}
              </div>
            </div>
          </validation-provider>

          <validation-provider
            :rules="'required|min_value:0|max_value:' + popupState.quantity"
            name="items-shortage"
            v-slot="{ errors }">
            <div class="w-100 mb-2">
              <d-input-group
                append="szt."
                prepend="BRAKI:"
                :state="errors.length ? 'invalid' : null">
                <d-input
                  type="number"
                  :min="0"
                  :step="1"
                  :max="popupState.quantity"
                  :state="errors.length ? 'invalid' : null"
                  class="text-right"
                  v-model="popupState.quantity_shortage" />
              </d-input-group>
              <div class="invalid-feedback text-center">
                {{ errors[0] }}
              </div>
            </div>
          </validation-provider>

          <validation-provider
            v-if="parseInt(popupState.quantity_shortage, 10) > 0"
            :rules="'required'"
            name="items-shortage"
            v-slot="{ errors }">
            <div class="w-100 mb-4">
              <d-textarea
                :value.prop="popupState.shortage_reason"
                v-model="popupState.shortage_reason"
                :state="errors.length ? 'invalid' : null"
                placeholder="CZEMU BRAK?">
              </d-textarea>
              <div class="invalid-feedback text-center">
                {{ errors[0] }}
              </div>
            </div>
          </validation-provider>

          <d-button
            theme="success"
            class="mt-2 w-100"
            size="sm"
            @click.prevent="validateForm"
            :disabled="popupState.saveInProgress">
            <div
              v-if="popupState.saveInProgress"
              class="spinner-border spinner-border-sm"
              role="status"></div>
            Aktualizuj
          </d-button>
        </validation-observer>
      </d-modal-body>
    </d-modal>
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import Vue from 'vue';

export default {
  name: 'dashboard',
  mixins: [
    HasFilters
  ],
  components: {
    'date-range-picker': DateRangePicker
  },
  filters: {
    dateStart (input) {
      if (input === null) {
        return ' od ';
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    dateEnd (input) {
      if (input === null) {
        return ' do ';
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    }
  },
  computed: {
    hasSewingItems () {
      return !!this.items.filter(item => item.process_type === 'SEWING').length;
    },
    hasPatternItems () {
      return !!this.items.filter(item => item.process_type === 'PATTERN').length;
    },
    sewingItems () {
      return this.items.filter(item => item.process_type === 'SEWING');
    },
    patternItems () {
      return this.items.filter(item => item.process_type === 'PATTERN');
    },
    sewingItemsCount () {
      return this.items.filter(item => item.process_type === 'SEWING').length;
    },
    patternItemsCount () {
      return this.items.filter(item => item.process_type === 'PATTERN').length;
    },
    hasEmergencyItems () {
      return !!this.emergencyItems.length;
    },
    emergencyItemsCount () {
      return this.emergencyItems.length;
    },
    missingItems () {
      return this.popupState.quantity - this.popupState.quantity_done;
    },
    currentUserName () {
      return this.$store.getters.getUserName;
    }
  },
  watch: {
    'visibleItems': function () {
      this.debouncedLoadData();
    },
    'range.startDate': function () {
      this.filters.date_from = new Date(this.range.startDate).toISOString().split('T')[0];
    },
    'range.endDate': function () {
      this.filters.date_to = new Date(this.range.endDate).toISOString().split('T')[0];
    }
  },
  data() {
    return {
      updatePopupVisible: false,
      items: [],
      emergencyItems: [],
      sewingDone: [],
      patternDone: [],
      loaded: false,
      emergencyLoaded: false,
      itemsScheduleOpened: {},
      itemsSewingScheduleOpened: {},
      itemsEmergencyScheduleOpened: {},
      emergencyVisible: localStorage.getItem('emergencyVisible') !== 'false',
      filters: {
        product_code: '',
        order_name: '',
        order_id: '',
        process_name: '',
        date_from: null,
        date_to: null
      },
      range: {
        startDate: null,
        endDate: null
      },
      popupState: {
        itemID: 0,
        quantity: 0,
        quantity_done: 0,
        quantity_additional: 0,
        quantity_shortage: 0,
        done: 1,
        shortage_reason: '',
        saveInProgress: false,
        isEmergency: false
      },
      showFilters: false,
      visibleItems: 'current',
      moreItemsExist: false
    };
  },
  mounted () {
    this.debouncedLoadData();
    this.debouncedLoadEmergencyData();
  },
  methods: {
    debouncedLoadData: debounce(function () {
      this.loadData();
    }, 500),
    debouncedLoadEmergencyData: debounce(function () {
      this.loadEmergencyData();
    }, 500),
    loadEmergencyData () {
      this.emergencyLoaded = false;

      FormUtils.submitInBackground(this, {
        endpoint: '/orders/products/processes/items/emergency-items',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Wczytywanie nie powiodło się. Spróbuj ponownie',
        successAction: (data) => {
          if (data.data) {
            this.emergencyItems = data.data;
          }

          this.emergencyLoaded = true;
        },
        failAction: () => {
          this.emergencyLoaded = true;
        }
      });
    },
    loadData () {
      this.loaded = false;
      let whereData = {};
      let endpointUrl = '/orders/products/processes/items/items';

      if (this.filters.product_code) {
        whereData.product_code = this.filters.product_code;
      }

      if (this.filters.order_name) {
        whereData.order_name = this.filters.order_name;
      }

      if (this.filters.order_id) {
        whereData.order_id = this.filters.order_id;
      }

      if (this.filters.process_name) {
        whereData.process_name = this.filters.process_name;
      }

      if (this.visibleItems === 'ended' && this.filters.date_from) {
        whereData.date_from = this.filters.date_from;
      }

      if (this.visibleItems === 'ended' && this.filters.date_to) {
        whereData.date_to = this.filters.date_to;
      }

      let paramsToSend = {
        where: whereData,
        pagination: 0
      };

      if (this.visibleItems === 'ended') {
        endpointUrl = '/orders/products/processes/items/ended';
        paramsToSend = {
          where: whereData,
          per_page: 50,
          pagination: true
        };
      }

      ListUtils.loadItemsData(this, {
        endpoint: endpointUrl,
        listField: ['items'],
        params: paramsToSend,
        pagination: false,
        successAction: (response) => {
          if (response.meta && response.meta.total > 50) {
            this.moreItemsExist = true;
          } else {
            this.moreItemsExist = false;
          }
        }
      });
    },
    dateColor (dateToCheck) {
      let timestamp = +new Date(dateToCheck);
      let currentTimestamp = +new Date();

      if (timestamp - currentTimestamp < 0) {
        return 'text-danger font-weight-bold';
      } else if (timestamp - currentTimestamp < 4 * 24 * 60 * 60 * 1000) {
        return 'text-warning';
      }

      return 'text-primary';
    },
    openPopup (item, isEmergency = false) {
      this.popupState.itemID = item.id;
      this.popupState.quantity = item.quantity;
      this.popupState.quantity_done = item.quantity_done;
      this.popupState.quantity_done_extra = item.quantity_additional;
      this.popupState.quantity_additional = 0;
      this.popupState.quantity_shortage = 0;
      this.popupState.shortage_reason = '';
      this.popupState.done = 1;
      this.popupState.saveInProgress = false;
      this.popupState.isEmergency = isEmergency;
      this.updatePopupVisible = true;
    },
    closePopup () {
      this.popupState.itemID = 0;
      this.popupState.quantity = 0;
      this.popupState.quantity_done = 0;
      this.popupState.quantity_done_extra = 0;
      this.popupState.quantity_additional = 0;
      this.popupState.quantity_shortage = 0;
      this.popupState.shortage_reason = '';
      this.popupState.done = 1;
      this.popupState.saveInProgress = false;
      this.popupState.isEmergency = false;
      this.updatePopupVisible = false;
    },
    validateForm () {
      FormUtils.validate(this.$refs['realisation-form'], this.submitForm);
    },
    submitForm () {
      this.popupState.saveInProgress = true;

      let formDataToSend = {
        execution: parseInt(this.popupState.done, 10),
        quantity_additional: parseInt(this.popupState.quantity_additional, 10),
        quantity_shortage: parseInt(this.popupState.quantity_shortage, 10)
      };

      if (parseInt(this.popupState.quantity_shortage, 10) > 0) {
        formDataToSend.shortage_reason = this.popupState.shortage_reason;
      }

      let endpointBase = this.popupState.isEmergency
        ? '/orders/products/processes/items/store-emergency-execution/'
        : '/orders/products/processes/items/store-execution/';
      let isEmergency = this.popupState.isEmergency;

      FormUtils.submit(this, {
        endpoint: endpointBase + parseInt(this.popupState.itemID, 10),
        formData: formDataToSend,
        successTitle: 'Zapisano stan',
        successText: 'Stan realizacji wybranej pozycji został zaktualizowany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja stanu nie powiodła się. Spróbuj ponownie.',
        beforeSuccessAction: () => {
          this.closePopup();
        },
        beforeFailAction: () => {
          this.closePopup();
        },
        successAction: () => {
          if (isEmergency) {
            this.debouncedLoadEmergencyData();
          } else {
            this.debouncedLoadData();
          }
        }
      });
    },
    toggleSchedule (items, index, value) {
      Vue.set(items, index, value);
    },
    toggleEmergencyVisible () {
      this.emergencyVisible = !this.emergencyVisible;
      localStorage.setItem('emergencyVisible', this.emergencyVisible ? 'true' : 'false');
    },
    addMissingItems () {
      this.popupState.done = this.missingItems;
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        product_code: '',
        order_name: '',
        order_id: '',
        process_name: ''
      };

      this.debouncedLoadData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      FormUtils.validate(this.$refs['process-items-filters-form'], this.debouncedLoadData);
    },
  }
};
</script>

<style lang="scss">
.items-list dl {
  display: flex;
  flex-wrap: wrap;
  font-size: 80%!important;
  margin: 0;
  width: 300px;

  dt {
    width: 140px;
  }
  dd {
    margin: 0;
    width: calc(100% - 140px);
  }
}

.input-group[state="invalid"] + .invalid-feedback {
  display: block;
}

.is-visible-on-mobile {
  display: none!important;
}

.realisation-form-popup.modal .input-group-prepend {
  min-width: 60%;

  .input-group-text {
    justify-content: flex-end;
    width: 100%;
  }
}

.flex-column-mobile {
  .vue-daterange-picker {
    width: calc(50% - 160px);
  }
}

@media (max-width: 1280px) {
  .is-hidden-on-mobile {
    display: none!important;
  }

  .is-visible-on-mobile {
    display: block!important;
  }

  .form-control {
    font-size: 16px!important;
  }
}

@media (max-width: 1024px) {
  .flex-column-mobile {
    flex-direction: column;
    padding-bottom: 15px;

    .col-2,
    .col-5,
    .col-6,
    .col-10 {
      flex-direction: row;
      flex: 0 0 100%;
      max-width: 100%!important;
      width: 100%!important;
    }

    .row.w-100 {
      padding-bottom: 20px!important;
      width: calc(100% + 20px) !important;
    }

    .vue-daterange-picker {
      width: calc(100% - 160px);
    }
  }
}

@media (max-width: 640px) {
  .page-header {
    padding-bottom: 10px!important;
    padding-top: 10px!important;
  }

  .page-header .page-title {
    font-size: 1.5rem!important;
  }

  .page-header .page-subtitle {
    font-size: .65rem!important;
  }

  .ui-title-text {
    margin-bottom: 0!important;
    text-align: left!important;
  }

  .ui-title-buttons {
    align-items: center;
    display: flex;
    justify-content: flex-end;

    .btn {
      margin-top: 0!important;
    }
  }

  .main-content > .main-content-container.container-fluid {
    padding-left: .75rem!important;
    padding-right: .75rem!important;
  }

  main > .main-content-container > .row.page-header:before {
    left: -0.75rem;
    width: calc(100% + 1.5rem);
  }

  .flex-column-mobile {
    .input-group {
      display: flex;
      flex-direction: column;
    }

    .input-group-prepend {
      display: none;
    }

    .vue-daterange-picker {
      width: calc(100% - 5px);
    }
  }
}

@media (max-width: 500px) {
  .page-header .page-title {
    font-size: 1.25rem!important;
  }

  .page-header .page-subtitle {
    font-size: .5rem!important;
  }

  .page-header .ui-button-label {
    display: none;
  }

  .row h4 {
    font-size: 20px;
  }

  .modal-header {
    padding-left: 1rem!important;
    padding-right: 1rem!important;
  }

  .modal-body.p-5 {
    padding: 1rem!important;
  }
}
</style>
